// Libraries imports
import { useEffect, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";

// Api, themes, assets, constants import
import { getUserRolesList, deleteUserRole, createUpdateRestaurantStaff, createUpdateUserRoles } from "../../../api";
import { THEME_MODE } from "../../../constants/Theme";
import { ReactComponent as PlusIcon } from "../../../assets/images/add_plus.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/images/delete1.svg";
import { ModalModes } from "./constants";
import {
    RESTAURANT_ADMIN_ROLE_ID,
    RESTAURANT_USER_ROLE_ID,
} from "../../../constants";

// Components imports
import Container from "../../../components/Container";
import Loader from "../../../components/Loader";
import RestaurantHeader from "../../../components/RestaurantHeader";
import UserTableList from "./UserTableList.tsx";
import AlertMsg from "../../../components/AlertMsg";
import AppDialog from "../../../components/AppDialog";

// Local component imports
import AddUser from "./AddUser";

const StaffManagement = ({ themeMode }) => {
    const theme_mode = THEME_MODE[themeMode ?? "light"];
    const styles = styles1(theme_mode);
    const navigate = useNavigate();
    const { rest_id } = useParams();

    const [loading, setLoading] = useState(true);
    const [userList, setUserList] = useState([]);
    const [userModalState, setUserModalState] = useState({ open: false });
    const [selectedUser, setSelectedUser] = useState(null);
    const [msgAlert, setMsgAlert] = useState({
        open: false,
        message: "",
        msgType: "error",
    });
    const [appDialogState, setAppDialogState] = useState({ open: false });

    const getUserList = useCallback(async () => {
        setLoading(true);
        const response = await getUserRolesList(rest_id, null);

        if (response.success) {
            const serverData = response.data.rows;
            if (serverData.length) {
                const list = [];
                serverData.forEach((x, i) => {
                    let data = {
                        _idRestaurant: x._idRestaurant,
                        _idUser: x._idUser,
                        _idUserRoles: x._idUserRoles,
                        userRoleId: x._id,
                        userRole:
                            x._idUserRoles === RESTAURANT_ADMIN_ROLE_ID
                                ? "Admin"
                                : x._idUserRoles === RESTAURANT_USER_ROLE_ID
                                    ? "User"
                                    : "",
                    };
                    if (x.userDetails?.length) {
                        let detail = x.userDetails[0];
                        data = {
                            ...data,
                            pin: detail?.pin ? detail.pin : "",
                            email: detail?.email ? detail.email : "",
                            name: detail?.name ? detail.name : "",
                            phoneNumber: detail?.phoneNumber ? detail.phoneNumber : "",
                            password: detail?.password ? detail.password : "",
                            isLocked: detail?.isLocked || false,
                            lockedReason: detail?.lockedReason || "",
                            lockedAt: detail?.lockedAt || "",
                            lastLoginAt: detail?.lastLoginAt || "",
                        };
                        list.push(data);
                    }
                });

                setUserList(list);
            }
        } else {
            setMsgAlert({ open: true, message: response.msg, msgType: "error" });
        }
        setLoading(false);
    }, [rest_id]);

    useEffect(() => {
        getUserList();
    }, [getUserList]);

    const closeUserModal = () => {
        setSelectedUser(null);
        setUserModalState({ ...userModalState, open: false });
    };

    const handleAddStaff = () => {
        setSelectedUser(null);
        setUserModalState({ open: true, mode: ModalModes.ADD });
    }

    const onEditUser = (userData) => {
        setSelectedUser(userData);
        setUserModalState({ open: true, mode: ModalModes.EDIT });
    };

    const onChangeRole = (userData) => {
        setSelectedUser(userData);
        setUserModalState({ open: true, mode: ModalModes.CHANGE_ROLE });
    };

    const onSubmitAddUser = async (data) => {
        setLoading(true);

        // Handle role change mode
        if (userModalState.mode === ModalModes.CHANGE_ROLE) {
            const payload = {
                _idUser: data._idUser,
                _idRestaurant: rest_id,
                _idUserRoles: data._idUserRoles,
            };

            const response = await createUpdateUserRoles(
                payload,
                data?.userRoleId ? data.userRoleId : null,
                rest_id
            );

            if (response.success) {
                const staffIndex = userList.findIndex((x) => x._idUser === data._idUser);
                if (staffIndex >= 0) {
                    const list = [...userList];
                    list[staffIndex] = { 
                        ...list[staffIndex], 
                        _idUserRoles: data._idUserRoles,
                        userRole: data.userRole
                    };
                    setUserList(list);
                }
                setMsgAlert({ open: true, message: "User Role Updated Successfully", msgType: "success" });
            } else {
                setMsgAlert({ open: true, message: response.msg, msgType: "error" });
            }
            setUserModalState({ ...userModalState, open: false });
            setLoading(false);
            return;
        }

        // Logic(to get staffData) copied from gosposh repo
        let staffData = {
            name: data.name,
            pin: data.pin,
        };

        if (data._idUserRoles === RESTAURANT_ADMIN_ROLE_ID || selectedUser?._idUser) {
            staffData = { ...staffData, email: data.email, phoneNumber: data.phoneNumber };
        }

        if (data?.password
            && ((data.password.trim() !== "" && data._idUserRoles === RESTAURANT_ADMIN_ROLE_ID)
                || data._idUserRoles === RESTAURANT_USER_ROLE_ID)
        ) staffData = { ...staffData, password: data.password };

        if (data._idUserRoles === RESTAURANT_USER_ROLE_ID) {
            staffData = { ...staffData, password: null };
        } else staffData._idRole = data._idUserRoles;

        if (selectedUser?._idUser) {
            delete staffData.email;
        }

        const response = await createUpdateRestaurantStaff(rest_id, staffData, data._idUser);

        if (response.success) {
            if (userModalState.mode === ModalModes.ADD) {
                createUserRoles(data._idUserRoles, response.data, data);
            } else {
                // For edit mode, also update the user role if it has changed
                if (selectedUser?._idUserRoles !== data._idUserRoles) {
                    const rolePayload = {
                        _idUser: data._idUser,
                        _idRestaurant: rest_id,
                        _idUserRoles: data._idUserRoles,
                    };

                    const roleResponse = await createUpdateUserRoles(
                        rolePayload,
                        selectedUser?.userRoleId ? selectedUser.userRoleId : null,
                        rest_id
                    );

                    if (roleResponse.success) {
                        const staffIndex = userList.findIndex((x) => x._idUser === data._idUser);
                        if (staffIndex >= 0) {
                            const list = [...userList];
                            list[staffIndex] = { 
                                ...list[staffIndex], 
                                ...data,
                                userRole: data._idUserRoles === RESTAURANT_ADMIN_ROLE_ID
                                    ? "Admin"
                                    : data._idUserRoles === RESTAURANT_USER_ROLE_ID
                                        ? "User"
                                        : ""
                            };
                            setUserList(list);
                        }
                        setMsgAlert({ open: true, message: "Staff Updated Successfully", msgType: "success" });
                    } else {
                        setMsgAlert({ open: true, message: roleResponse.msg, msgType: "error" });
                    }
                } else {
                    // Role didn't change, just update the staff info
                    const staffIndex = userList.findIndex((x) => x._idUser === data._idUser);
                    if (staffIndex >= 0) {
                        const list = [...userList];
                        list[staffIndex] = { 
                            ...list[staffIndex], 
                            ...data,
                            userRole: data._idUserRoles === RESTAURANT_ADMIN_ROLE_ID
                                ? "Admin"
                                : data._idUserRoles === RESTAURANT_USER_ROLE_ID
                                    ? "User"
                                    : ""
                        };
                        setUserList(list);
                    }
                    setMsgAlert({ open: true, message: "Staff Updated Successfully", msgType: "success" });
                }
                setUserModalState({ ...userModalState, open: false });
                setLoading(false);
            }
        } else {
            setMsgAlert({ open: true, message: response.msg, msgType: "error" });
            setUserModalState({ ...userModalState, open: false, })
            setLoading(false);
        }
    };

    const createUserRoles = async (roleId, data) => {
        const payload = {
            _idUser: data._id,
            _idRestaurant: rest_id,
            _idUserRoles: roleId,
        };

        const response = await createUpdateUserRoles(
            payload,
            data?.userRoleId ? data.userRoleId : null,
            rest_id
        );

        if (response.success) {
            setUserList([...userList, { ...data, ...payload }]);
            setMsgAlert({ open: true, message: "Staff Added Successfully", msgType: "success" });
        } else {
            setMsgAlert({ open: true, message: response.msg, msgType: "error" });
        }
        setUserModalState({ ...userModalState, open: false });
        setLoading(false);
    };

    const closeAppDialog = () => {
        if (loading) return;
        setAppDialogState({ ...appDialogState, open: false });
    }

    const handleDeleteClick = (staff) => {
        setAppDialogState({
            open: true,
            title: (
                <Box>
                    <Typography sx={{ margin: 0 }}>
                        <DeleteIcon width={"2.5rem"} height={"2.5rem"} />
                    </Typography>
                    <Typography
                        variant="h2"
                        sx={{ fontSize: "1.5rem", fontFamily: "GilroySemiBold" }}
                    >
                        Delete Staff Record?
                    </Typography>
                </Box>
            ),
            content: (
                <Box>
                    Are you sure you want to delete this staff record with name {staff.name}? This action
                    cannot be undone.
                </Box>
            ),
            firstBtnText: "Cancel",
            secondBtnText: "Delete",
            onClose: closeAppDialog,
            onConfirm: () => onConfirmDelete(staff),
        });
    };

    const onConfirmDelete = async (staff) => {
        setLoading(true);
        const response = await deleteUserRole(staff._idUser, rest_id);
        if (response.success) {
            setUserList(userList.filter((x) => x._idUser !== staff._idUser));
            setMsgAlert({ open: true, message: "Staff Deleted Successfully", msgType: "success" });
        } else {
            setMsgAlert({ open: true, message: response.msg, msgType: "error" });
        }
        setLoading(false);
        setAppDialogState({ ...appDialogState, open: false });
    };

    return (
        <Container
            page={"restaurants"}
            searchVal={""}
            onSearch={() => null}
            searchLabel=""
            p={1.5}
        >
            {loading ? (
                <Box sx={{ width: '100%', height: 'calc(100vh - 7rem)', display: "flex", justifyContent: "center", alignItems: "center" }}><Loader /></Box>
            ) : (
                <Grid
                    p={1.5}
                    style={{ backgroundColor: theme_mode.card3, borderRadius: 15 }}
                >
                    <Box sx={styles.titleMainBox}>
                        <RestaurantHeader
                            title={"Staff Management"}
                            onBackBtnClick={() => navigate("/restaurant/" + rest_id)}
                        />
                        <Button sx={styles.createButton} onClick={handleAddStaff}>
                            <PlusIcon />
                            Add Staff
                        </Button>
                    </Box>
                    <Grid>
                        <UserTableList
                            themeMode={themeMode}
                            itemList={userList}
                            editItem={onEditUser}
                            changeRole={onChangeRole}
                            deleteItem={handleDeleteClick}
                            onLockStatusChange={getUserList}
                        />
                    </Grid>
                </Grid >
            )}
            {
                userModalState.open && < AddUser
                    open={userModalState.open}
                    onClose={closeUserModal}
                    onSave={onSubmitAddUser}
                    userData={selectedUser}
                    mode={userModalState.mode}
                    loading={loading}
                />
            }
            <AppDialog
                open={appDialogState.open}
                onClose={appDialogState.onClose}
                content={appDialogState.content}
                title={appDialogState.title}
                firstBtnContent={appDialogState.firstBtnText}
                secondBtnContent={"Delete"}
                firstBtnClickHandler={appDialogState.onClose}
                secondBtnClickHandler={appDialogState.onConfirm}
                options={{ btnLoading: loading }}
            />
            <AlertMsg
                msgAlert={msgAlert}
                onCloseAlertMsg={() => setMsgAlert({ ...msgAlert, open: false })}
            />
        </Container >
    );
};

const mapStateToProps = (state) => {
    return {
        themeMode: state.userData.themeMode,
        pinUserId: state.userData.pinUserId,
        userId: state.userData.userId,
        userRolesList: state.userData.userRolesList,
    };
};

export default connect(mapStateToProps)(StaffManagement);

const styles1 = (Theme) => ({
    titleMainBox: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 2,
    },
    createButton: {
        backgroundColor: "#000",
        color: "#FFF",
        fontFamily: "GilroyBold",
        fontSize: 14,
        borderRadius: 1,
        "&:hover": {
            backgroundColor: "#000 !important",
        },
    },
    addBtnView: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: Theme.black,
        border: `1.5px solid ${Theme.border7}`,
        borderRadius: "9px",
        cursor: "pointer",
        ml: 1,
        px: 3,
        py: 1,
    },
    addBtnText: {
        color: Theme.btnText,
        fontSize: 13,
        fontFamily: "InterSemiBold",
        marginLeft: 1,
    },
});
